import React from 'react';
import Chart from 'chart.js';
import { useTheme, makeStyles, Theme } from '@material-ui/core/styles';
import grey from '@material-ui/core/colors/grey';

const getBorderColor = (value) => value || grey[700];

const getBgColor = (value) =>
  Chart.helpers
    .color(value || grey[700])
    ?.alpha(0.4)
    ?.rgbString();

const getLabels = (values) => {
  const labels = values.map((v) => (Array.isArray(v) ? v.join('') : v));
  if (window.innerWidth > 600) {
    return labels.map((v) => v.match(/.{1,6}/g));
  } else {
    const right = Math.trunc(labels.length / 4);
    const left = Math.trunc((labels.length * 3) / 4);
    const sides = [right, left];
    if (labels.length > 8) {
      const extras = [right + 1, left + 1];
      labels.length % 4 === 0 && extras.push(right - 1, left - 1);
      sides.push(...extras);
    } else if (labels.length % 4 !== 0) {
      sides.push(right + 1, left + 1);
    }
    return labels.map((v, idx) => {
      if (sides.includes(idx)) {
        return v.match(/.{1,3}/g);
      }
      return v.match(/.{1,6}/g);
    });
  }
};

const chartOptions = (
  theme: Theme,
  data: Array<number>,
  backgroundColor: string,
  labels: Array<string | Array<string>>,
) =>
  ({
    type: 'radar',
    data: {
      datasets: [
        {
          data,
          backgroundColor: getBgColor(backgroundColor),
          pointHitRadius: 5,
          borderColor: getBorderColor(backgroundColor),
        },
      ],
      labels: getLabels(labels),
    },
    options: {
      responsive: true,
      aspectRatio: 1,
      onResize: (chart, size) => {
        chart.data.labels = getLabels(chart.data.labels);
        chart.update();
      },
      tooltips: {
        titleFontFamily: theme.typography.fontFamily,
        bodyFontFamily: theme.typography.fontFamily,
        footerFontFamily: theme.typography.fontFamily,
        displayColors: false,
        callbacks: {
          title: () => '',
          label: (tooltipItem, data) => {
            const value = data.labels[tooltipItem.index] || '';
            return Array.isArray(value) ? value.join('') : value;
          },
        },
      },
      legend: { display: false },
      scale: {
        angleLines: {
          color: 'rgba(0, 0, 0, 0.2)',
        },
        gridLines: {
          color: 'rgba(0, 0, 0, 0.2)',
          circular: true,
        },
        ticks: {
          suggestedMin: 0,
          suggestedMax: 5,
          stepSize: 1,
          display: false,
        },
        pointLabels: {
          fontSize: 11,
          fontColor: theme.palette.text.primary,
        },
      },
      plugins: {
        datalabels: {
          labels: {
            display: false,
          },
        },
      },
    },
  } as any); // onResizeで型定義がはまっていないが、一旦anyで無視する

const useStyles = makeStyles((theme) => ({
  chart: {
    margin: 'auto',
    width: '100%',
    maxWidth: 350,
    [theme.breakpoints.up('sm')]: {
      margin: theme.spacing('auto', 'auto', 'auto', 0),
      maxWidth: 400,
    },
  },
}));

interface Props {
  options: Array<{ name: string; portion: number }>;
  bgColor: string;
}

const RadarChart = ({ options = [], bgColor }: Props) => {
  const canvasRef = React.useRef<any>();
  const chartRef = React.useRef<any>();
  const theme = useTheme();

  const classes = useStyles();

  React.useEffect(() => {
    const data: Array<number> = [],
      labels: Array<string> = [];

    options.forEach(({ portion, name }) => {
      data.push(portion);
      labels.push(name);
    });

    if (chartRef.current) {
      const chart = chartRef.current;
      chart.data.datasets[0].data = data;
      chart.data.datasets[0].backgroundColor = getBgColor(bgColor);
      chart.data.datasets[0].borderColor = getBorderColor(bgColor);
      chart.data.labels = getLabels(labels);
      chart.update();
    } else {
      const ctx = canvasRef.current?.getContext('2d');
      chartRef.current = new Chart(
        ctx,
        chartOptions(theme, data, bgColor, labels),
      );
    }
  }, [options, bgColor, theme]);

  React.useEffect(() => () => chartRef.current?.destroy?.(), []);

  return (
    <div>
      <div className={classes.chart}>
        <canvas ref={canvasRef} />
      </div>
    </div>
  );
};

export default RadarChart;
